import { Link } from "react-router-dom";
import { GCSEOHead, CTABanner, LastUpdated } from "./GCLayout";
import { JsonLd } from "../../components/JsonLd";
import { gcPath, gcCanonicalBase } from "./gcDomain";
import {
  FileText, Clock, CheckSquare, HelpCircle, ArrowRight,
  Shield, Users, Globe, Zap,
} from "lucide-react";

const FEATURES = [
  {
    icon: FileText,
    title: "Green Card Guide",
    desc: "Complete walkthrough of every Green Card category, from family-based to employment-based.",
    link: gcPath("/green-card-guide"),
  },
  {
    icon: Clock,
    title: "Processing Timeline",
    desc: "Realistic timelines for each step, from petition filing to card-in-hand.",
    link: gcPath("/green-card-timeline"),
  },
  {
    icon: CheckSquare,
    title: "Documents Checklist",
    desc: "Every document you need, organized by category. Never miss a filing requirement.",
    link: gcPath("/documents-checklist"),
  },
  {
    icon: HelpCircle,
    title: "FAQ",
    desc: "Answers to the most common questions about the Green Card process.",
    link: gcPath("/faq"),
  },
];

const STATS = [
  { value: "1M+", label: "Green Cards issued annually" },
  { value: "6-24 mo", label: "Typical processing time" },
  { value: "140K", label: "Employment-based visas/year" },
  { value: "226K", label: "Family-based visas/year" },
];

const LANDING_FAQS = [
  {
    q: "How long does it take to get a Green Card?",
    a: "It depends on the category. Immediate relatives of U.S. citizens typically wait 12-18 months. Employment-based applicants may wait 2-10+ years depending on country of birth. DV Lottery winners usually complete the process in 12-18 months after selection.",
  },
  {
    q: "What documents do I need for a Green Card?",
    a: "At minimum: valid passport, birth certificate, passport photos, Form I-485, medical exam (I-693), financial evidence (tax returns, pay stubs), and police clearance certificates. A complete checklist covers 32 documents across 7 categories.",
  },
  {
    q: "Can I apply for a Green Card without a lawyer?",
    a: "Yes. Many people successfully file on their own, especially for straightforward family-based or DV Lottery cases. Clear guides, a document checklist, and deadline tracking help you stay organized without legal fees.",
  },
  {
    q: "What is the DV Lottery?",
    a: "The Diversity Visa Lottery is a free annual program that makes 55,000 Green Cards available to people from countries with low U.S. immigration rates. You need a high school diploma or qualifying work experience to apply.",
  },
];

export default function GCLanding() {
  return (
    <>
      <GCSEOHead
        title="U.S. Green Card Guide 2026 - Free Step-by-Step Process"
        description="Free 2026 guide to the U.S. Green Card process - 6 categories, real timelines, 32-document checklist, and step-by-step instructions for permanent residency."
        path="/"
      />

      {/* JSON-LD WebSite Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Green Card Guide",
            url: gcCanonicalBase(),
            description: "Free 2026 guide to the U.S. Green Card process.",
            dateModified: "2026-02-01",
            publisher: { "@type": "Organization", name: "ImmigrationGuide", url: "https://immigrationguide.us" },
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: LANDING_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-emerald-50 to-white py-16 md:py-24" data-testid="gc-hero">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <Globe className="h-4 w-4" /> Free Educational Resource
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-heading leading-tight mb-5">
            Your Complete Guide to the{" "}
            <span className="text-emerald-600">U.S. Green Card</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Eligibility requirements, realistic processing timelines, and a full document checklist —
            organized by Green Card category so you know exactly where you stand.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={gcPath("/green-card-guide")}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3.5 rounded-lg text-base transition-colors shadow-md"
              data-testid="gc-hero-cta"
            >
              Read the Guide <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://immigrationguide.us/auth?mode=register"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium px-8 py-3.5 rounded-lg text-base transition-colors"
              data-testid="gc-hero-platform"
            >
              Try AI-Powered Tools
            </a>
          </div>
          <div className="mt-6 flex justify-center">
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-emerald-600 font-heading">{s.value}</div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-20" data-testid="gc-features">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading mb-3">
              Everything You Need to Know
            </h2>
            <p className="text-base text-slate-600 max-w-xl mx-auto">
              We broke down the complex Green Card process into clear, actionable sections.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <Link
                key={f.title}
                to={f.link}
                className="group p-6 rounded-xl border border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all"
                data-testid={`gc-feature-${f.link.split("/").pop()}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                    <f.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-emerald-700 transition-colors">
                      {f.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="py-8" data-testid="gc-mid-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-600 text-sm">
            Not sure where to start?{" "}
            <a
              href="https://immigrationguide.us/auth?mode=register"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 font-medium hover:text-emerald-700 underline underline-offset-2"
            >
              Check your case in plain English
            </a>{" "}
            with our free AI tool.
          </p>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center gap-3">
              <Shield className="h-8 w-8 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Based on Official USCIS Data</h3>
              <p className="text-sm text-slate-500">All information sourced from official U.S. government resources.</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Users className="h-8 w-8 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Written for Real People</h3>
              <p className="text-sm text-slate-500">No legal jargon. Clear language that anyone can understand.</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Zap className="h-8 w-8 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Always Up to Date</h3>
              <p className="text-sm text-slate-500">Regularly updated to reflect the latest USCIS policy changes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* DV Lottery Resources */}
      <section className="py-10 border-b border-slate-100" data-testid="gc-dv-resources">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">DV Lottery Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="https://immigrationguide.us/how-to-check-dv-lottery-results"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all"
              data-testid="gc-link-dv-results"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">
                How to Check DV Lottery Results
              </h3>
              <p className="text-xs text-slate-500">Official steps to verify your Diversity Visa selection status</p>
            </a>
            <a
              href="https://immigrationguide.us/dv-lottery-eligibility"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all"
              data-testid="gc-link-dv-eligibility"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">
                DV Lottery Eligibility Requirements
              </h3>
              <p className="text-xs text-slate-500">Country, education, and work experience requirements</p>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16" data-testid="gc-landing-faq">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {LANDING_FAQS.map((faq) => (
              <div key={faq.q}>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
